import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PricingTable } from '../components/PricingTable';
import { PremiumBadge } from '../components/PremiumBadge';
import { paymentsService } from '../services/payments.service';

export const PremiumPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async () => {
    if (user?.isPremium) {
      navigate('/subscription');
      return;
    }

    setIsLoading(true);
    try {
      const { url } = await paymentsService.createCheckoutSession();
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <button onClick={handleGoBack} style={styles.backButton}>
            ← Back
          </button>

          {user?.isPremium ? (
            <>
              <div style={styles.premiumStatusContainer}>
                <PremiumBadge size="large" />
                <h1 style={styles.title}>You're Premium!</h1>
                <p style={styles.subtitle}>
                  Thank you for being a Premium member. You have access to all features.
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--color-soft-blue)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '1rem',
                  }}
                >
                  Manage Subscription
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 style={styles.title}>Unlock Premium Features</h1>
              <p style={styles.subtitle}>
                Get unlimited connections and exclusive features for just $9.99/month
              </p>
            </>
          )}
        </div>

        {/* Benefits Section */}
        {!user?.isPremium && (
          <div style={styles.benefitsSection}>
            <h2 style={styles.sectionTitle}>Why Upgrade to Premium?</h2>
            <div style={styles.benefitsGrid}>
              <div style={styles.benefitCard}>
                <div style={styles.benefitIcon}>💬</div>
                <h3 style={styles.benefitTitle}>Unlimited Sessions</h3>
                <p style={styles.benefitDescription}>
                  Chat as much as you want, every day. No daily limits.
                </p>
              </div>

              <div style={styles.benefitCard}>
                <div style={styles.benefitIcon}>⏱️</div>
                <h3 style={styles.benefitTitle}>No Time Limits</h3>
                <p style={styles.benefitDescription}>
                  Have meaningful conversations without worrying about the clock.
                </p>
              </div>

              <div style={styles.benefitCard}>
                <div style={styles.benefitIcon}>⏭️</div>
                <h3 style={styles.benefitTitle}>Skip to Next</h3>
                <p style={styles.benefitDescription}>
                  Not vibing? Instantly skip to your next match.
                </p>
              </div>

              <div style={styles.benefitCard}>
                <div style={styles.benefitIcon}>🌍</div>
                <h3 style={styles.benefitTitle}>Global Matching</h3>
                <p style={styles.benefitDescription}>
                  Match with people from any country, expanding your global connections.
                </p>
              </div>

              <div style={styles.benefitCard}>
                <div style={styles.benefitIcon}>🚀</div>
                <h3 style={styles.benefitTitle}>Priority Matching</h3>
                <p style={styles.benefitDescription}>
                  Get matched instantly - jump to the front of the queue.
                </p>
              </div>

              <div style={styles.benefitCard}>
                <div style={styles.benefitIcon}>⚡</div>
                <h3 style={styles.benefitTitle}>Instant Connect</h3>
                <p style={styles.benefitDescription}>
                  No 30-second wait time. Start chatting immediately.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Table */}
        <div style={styles.pricingSection}>
          <PricingTable onUpgradeClick={handleUpgrade} />
        </div>

        {/* Testimonials Section */}
        {!user?.isPremium && (
          <div style={styles.testimonialsSection}>
            <h2 style={styles.sectionTitle}>What Premium Users Say</h2>
            <div style={styles.testimonialsGrid}>
              <div style={styles.testimonialCard}>
                <div style={styles.testimonialQuote}>"</div>
                <p style={styles.testimonialText}>
                  "Premium is worth every penny! I've had so many amazing conversations without
                  time limits. The skip feature is a game-changer."
                </p>
                <p style={styles.testimonialAuthor}>- Sarah K.</p>
              </div>

              <div style={styles.testimonialCard}>
                <div style={styles.testimonialQuote}>"</div>
                <p style={styles.testimonialText}>
                  "Priority matching means I never wait. I can chat whenever I want, for as
                  long as I want. Best decision ever!"
                </p>
                <p style={styles.testimonialAuthor}>- Mike D.</p>
              </div>

              <div style={styles.testimonialCard}>
                <div style={styles.testimonialQuote}>"</div>
                <p style={styles.testimonialText}>
                  "Unlimited sessions means I can connect whenever I want. The ad-free
                  experience is absolutely worth it!"
                </p>
                <p style={styles.testimonialAuthor}>- Emily R.</p>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div style={styles.faqSection}>
          <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
          <div style={styles.faqList}>
            <div style={styles.faqItem}>
              <h3 style={styles.faqQuestion}>Can I cancel anytime?</h3>
              <p style={styles.faqAnswer}>
                Yes! You can cancel your subscription at any time with no penalties. You'll
                continue to have Premium access until the end of your billing period.
              </p>
            </div>

            <div style={styles.faqItem}>
              <h3 style={styles.faqQuestion}>How does billing work?</h3>
              <p style={styles.faqAnswer}>
                You'll be charged $9.99/month automatically. You can view and manage your
                subscription in your account settings.
              </p>
            </div>

            <div style={styles.faqItem}>
              <h3 style={styles.faqQuestion}>What payment methods do you accept?</h3>
              <p style={styles.faqAnswer}>
                We accept all major credit cards and debit cards through our secure payment
                processor, Stripe.
              </p>
            </div>

            <div style={styles.faqItem}>
              <h3 style={styles.faqQuestion}>Is there a free trial?</h3>
              <p style={styles.faqAnswer}>
                Currently, we don't offer a free trial, but you can try VibeTalk for free
                with 3 sessions per day (10 minutes each) to see if you like it before upgrading.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-soft-cream)',
    padding: '2rem',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '3rem',
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '1rem',
    color: 'var(--color-dark-navy)',
    fontWeight: '500',
  },
  premiumStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  title: {
    fontSize: '3rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '0.5rem',
    margin: 0,
  },
  subtitle: {
    fontSize: '1.2rem',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  benefitsSection: {
    marginBottom: '4rem',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    textAlign: 'center',
    marginBottom: '2rem',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
  },
  benefitCard: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    textAlign: 'center',
    boxShadow: 'var(--shadow-md)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  benefitIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  benefitTitle: {
    fontSize: '1.4rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    marginBottom: '0.75rem',
  },
  benefitDescription: {
    fontSize: '1rem',
    color: 'var(--color-gray-600)',
    lineHeight: '1.6',
  },
  pricingSection: {
    marginBottom: '4rem',
  },
  testimonialsSection: {
    marginBottom: '4rem',
  },
  testimonialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
  },
  testimonialCard: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    boxShadow: 'var(--shadow-md)',
    position: 'relative',
  },
  testimonialQuote: {
    fontSize: '4rem',
    color: 'var(--color-warm-orange)',
    opacity: 0.3,
    position: 'absolute',
    top: '0.5rem',
    left: '1rem',
    lineHeight: 1,
  },
  testimonialText: {
    fontSize: '1rem',
    color: 'var(--color-gray-700)',
    lineHeight: '1.6',
    fontStyle: 'italic',
    marginBottom: '1rem',
    paddingTop: '2rem',
  },
  testimonialAuthor: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    margin: 0,
  },
  faqSection: {
    marginBottom: '4rem',
  },
  faqList: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  faqItem: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: 'var(--shadow-sm)',
  },
  faqQuestion: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    marginBottom: '0.75rem',
  },
  faqAnswer: {
    fontSize: '1rem',
    color: 'var(--color-gray-700)',
    lineHeight: '1.6',
    margin: 0,
  },
};
